import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useCreateBudgetYear,
  useFinanceSettingsStore
} from "features/finances";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { EthiopianDateField } from "../EthiopianDateField";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const budgetYearSchema = yup.object({
  title: yup.string().required("Title is required"),
  startDate: yup.string().required("Start date is required"),
  endDate: yup.string().required("End date is required"),
  description: yup.string().optional()
});

export function CreateBudgetYearModal() {
  const createBudgetYearOpen = useFinanceSettingsStore(
    state => state.createBudgetYearOpen
  );
  const closeCreateBudgetYearModal = useFinanceSettingsStore(
    state => state.closeCreateBudgetYearModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const createBudgetYearMutation = useCreateBudgetYear();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: "",
      startDate: "",
      endDate: "",
      description: ""
    },
    resolver: yupResolver(budgetYearSchema)
  });

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const onSubmit = values => {
    const budgetYearData = {
      title: values.title,
      start_date: values.startDate,
      end_date: values.endDate,
      description: values.description
    };

    createBudgetYearMutation.mutate(
      {
        churchId: selectedChurch?.id,
        budgetYearData
      },
      {
        onSuccess: () => {
          reset();
        }
      }
    );
  };

  return (
    <BaseModal
      title="Create Budget Year"
      isOpen={createBudgetYearOpen}
      onClose={closeCreateBudgetYearModal}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            flavor="white"
            size="small"
            onClick={closeCreateBudgetYearModal}
            disabled={createBudgetYearMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmit)}
            disabled={createBudgetYearMutation.isLoading}
          >
            Create Budget Year
          </Button>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="name">Title</Label>
        <Input {...register("title")} />
        <ErrorMessage
          errors={errors}
          name="title"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="startDate">Start Date</Label>
        <EthiopianDateField
          size="small"
          value={startDate}
          onChange={e => setValue("startDate", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="startDate"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="endDate">End Date</Label>
        <EthiopianDateField
          size="small"
          value={endDate}
          onChange={e => setValue("endDate", e.target.value)}
        />
        <ErrorMessage
          errors={errors}
          name="endDate"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="description">Description (optional)</Label>
        <Input {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>

      <MutationError error={createBudgetYearMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
